/**
 * Favorites Service - API calls for favorite candidate CRUD operations
 *
 * PURPOSE:
 * Provides a clean interface for favorite candidate API operations.
 * Favorites are references (configurationId + shapeId) - no hydration needed.
 *
 * PATTERN:
 * - Returns `data | null` (null on any error)
 * - Uses logger for error reporting
 *
 * API ENDPOINTS:
 * - GET    /api/projects/:projectId/favorites                           → List favorites
 * - POST   /api/projects/:projectId/favorites                           → Add favorite
 * - DELETE /api/projects/:projectId/favorites                           → Remove favorite
 * - GET    /api/projects/:projectId/configurations/:configId/favorites  → Get config favorites count
 */

import type { FavoriteCandidateInfo } from '@shared/models';
import { logger } from '~/services/logging';

// ============================================
// API Methods
// ============================================

/**
 * Get all favorites for a project
 */
export async function getFavorites(projectId: string): Promise<FavoriteCandidateInfo[] | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}/favorites`);

    if (!response.ok) {
      logger.L2.debug('[FavoritesService] Failed to fetch favorites', {
        context: 'api:favorites',
        projectId,
        status: response.status,
      });
      return null;
    }

    const { favorites } = (await response.json()) as { favorites: FavoriteCandidateInfo[] };
    return favorites;
  } catch (error) {
    logger.L2.debug('[FavoritesService] Error fetching favorites', {
      context: 'api:favorites',
      projectId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Add a favorite
 * Returns the created favorite (idempotent - returns existing if already favorited)
 */
export async function addFavorite(
  projectId: string,
  configurationId: string,
  shapeId: string
): Promise<FavoriteCandidateInfo | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}/favorites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ configurationId, shapeId }),
    });

    if (!response.ok) {
      logger.L2.debug('[FavoritesService] Failed to add favorite', {
        context: 'api:favorites',
        projectId,
        configurationId,
        shapeId,
        status: response.status,
      });
      return null;
    }

    const { favorite } = (await response.json()) as { favorite: FavoriteCandidateInfo };
    return favorite;
  } catch (error) {
    logger.L2.debug('[FavoritesService] Error adding favorite', {
      context: 'api:favorites',
      projectId,
      configurationId,
      shapeId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Remove a favorite
 * Returns true on success, false on failure
 */
export async function removeFavorite(
  projectId: string,
  configurationId: string,
  shapeId: string
): Promise<boolean> {
  try {
    const response = await fetch(`/api/projects/${projectId}/favorites`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ configurationId, shapeId }),
    });

    if (!response.ok) {
      logger.L2.debug('[FavoritesService] Failed to remove favorite', {
        context: 'api:favorites',
        projectId,
        configurationId,
        shapeId,
        status: response.status,
      });
      return false;
    }

    return true;
  } catch (error) {
    logger.L2.debug('[FavoritesService] Error removing favorite', {
      context: 'api:favorites',
      projectId,
      configurationId,
      shapeId,
      error: String(error),
    });
    return false;
  }
}

/**
 * Get favorites for a specific configuration (for deletion warning)
 * Returns count and list of favorites
 */
export async function getFavoritesForConfiguration(
  projectId: string,
  configId: string
): Promise<{ favorites: FavoriteCandidateInfo[]; count: number } | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}/configurations/${configId}/favorites`);

    if (!response.ok) {
      logger.L2.debug('[FavoritesService] Failed to fetch config favorites', {
        context: 'api:favorites',
        projectId,
        configId,
        status: response.status,
      });
      return null;
    }

    const data = (await response.json()) as { favorites: FavoriteCandidateInfo[]; count: number };
    return data;
  } catch (error) {
    logger.L2.debug('[FavoritesService] Error fetching config favorites', {
      context: 'api:favorites',
      projectId,
      configId,
      error: String(error),
    });
    return null;
  }
}

// ============================================
// Export as namespace-like object
// ============================================

export const FavoritesService = {
  getFavorites,
  addFavorite,
  removeFavorite,
  getFavoritesForConfiguration,
};
